// Data seam: sphinx REST (/api/sphinx/eval-suites, /cases, /runs,
// /iterations). The Convex testSuites:* shapes the eval components consume
// (`EvalSuite`, `EvalCase`, `EvalIteration`, `EvalSuiteRun`) are rebuilt here
// from the REST rows; nothing outside this module talks to the API.
//
// Decisions:
// - suites / cases / runs / iterations → REST. A case's `EvalCase` fields
//   (title, query, steps, …) are stored verbatim as the case row's `json`;
//   suite fields with no column (description, environment, defaultConfig,
//   judgeConfig, hostAttachments, …) are kept in localStorage
//   (`sphinx:suiteMeta:<id>`) and merged over the REST row on read.
// - run `status`/iteration `status` map sphinx vocabulary → Convex vocabulary
//   (complete→completed, canceled→cancelled); iteration `result` is derived
//   from `grade` (`passed`/`pass`/`verdict`).
// - live views poll every 2s while any run/iteration is pending/running; the
//   initiating tab's SSE stream is untouched.
// - getTestIterationBlob → the iteration's `clientTranscript`.
// - getTestSuiteRunDiff, traceExport:*, attachments upload → unsupported
//   (reject with a clear message).
// - saveAsTestCaseFromChatMessage / importChatSessionToTestCase → create a
//   case (and optionally a suite) through REST from the given title/query.
import type {
  EvalCase,
  EvalIteration,
  EvalSuite,
  EvalSuiteOverviewEntry,
  EvalSuiteRun,
  SuiteDetailsQueryResponse,
} from "@/components/evals/types";
import {
  sphinxApi,
  type SphinxCase,
  type SphinxIteration,
  type SphinxRun,
  type SphinxSuite,
  type SphinxSuiteSummary,
} from "@/lib/sphinx-api";
import { useSphinxQuery, withInvalidate } from "@/lib/use-sphinx-query";
import type { TargetId, YarnEnv, YarnOrg } from "@/shared/sphinx";
import type { DataClient } from "./client";
import { readLocal, writeLocal } from "./local-store";
import { DEFAULT_PROJECT_ID } from "./project";
import { OPERATOR_USER } from "./user";

const LIVE_POLL_MS = 2000;
const RUNS_WITH_ITERATIONS_CAP = 25;

// ---- helpers ----------------------------------------------------------------

const toMs = (iso: string | null | undefined) => {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? ms : 0;
};

const isLive = (status: string) => status === "pending" || status === "running";

type RunStatusOut = EvalSuiteRun["status"];
type IterationStatusOut = EvalIteration["status"];
type IterationResultOut = EvalIteration["result"];

const mapRunStatus = (s: string): RunStatusOut =>
  s === "complete" ? "completed" : s === "canceled" ? "cancelled" : (s as RunStatusOut);

const mapIterationStatus = (s: string): IterationStatusOut =>
  s === "complete" ? "completed" : s === "canceled" ? "cancelled" : (s as IterationStatusOut);

function gradePassed(grade: unknown): boolean | null {
  if (!grade || typeof grade !== "object") return null;
  const g = grade as Record<string, unknown>;
  if (typeof g.passed === "boolean") return g.passed;
  if (typeof g.pass === "boolean") return g.pass;
  if (typeof g.verdict === "string") return g.verdict === "pass";
  if (typeof g.score === "number" && typeof g.threshold === "number")
    return g.score >= g.threshold;
  return null;
}

function deriveResult(it: SphinxIteration): IterationResultOut {
  switch (it.status) {
    case "complete": {
      const passed = gradePassed(it.grade);
      return passed === false ? "failed" : passed === true ? "passed" : "pending";
    }
    case "failed":
      return "failed";
    case "canceled":
      return "cancelled";
    default:
      return "pending";
  }
}

// ---- suite meta (localStorage) ---------------------------------------------

type SuiteMeta = Partial<Omit<EvalSuite, "_id" | "name" | "createdAt" | "updatedAt">>;
const suiteMetaKey = (id: string) => `suiteMeta:${id}`;
const NO_META: SuiteMeta = {};
const readSuiteMeta = (id: string) => readLocal<SuiteMeta>(suiteMetaKey(id), NO_META);
const writeSuiteMeta = (id: string, patch: SuiteMeta) =>
  writeLocal(suiteMetaKey(id), { ...readSuiteMeta(id), ...patch });

// ---- row → Convex-shaped mapping -------------------------------------------

/**
 * The suite UI's attachment picker saves drafts shaped `{ namedHostId,
 * enabledOptionalServerIds }`; the fork's Convex mutation enriched those
 * server-side with `hostName` / `resolvedServerNames`, and every reader
 * assumes the full shape (`attachment.resolvedServerNames.length` crashed the
 * whole Evaluate tab — "Could not load Testing"). sphinx stores the draft
 * verbatim in localStorage, so the missing fields are defaulted here, at the
 * one read chokepoint — which also heals metas persisted before this fix.
 * Empty `resolvedServerNames` is sound: run plans fall back to the suite's
 * effective servers, and sphinx runs derive connectors from the target rows
 * server-side anyway.
 */
function normalizeHostAttachments(
  attachments: EvalSuite["hostAttachments"]
): EvalSuite["hostAttachments"] {
  if (!Array.isArray(attachments)) return attachments;
  return attachments.map((attachment) => ({
    ...attachment,
    hostName: attachment.hostName ?? null,
    resolvedServerNames: attachment.resolvedServerNames ?? [],
    enabledOptionalServerIds: attachment.enabledOptionalServerIds ?? [],
  }));
}

export function toEvalSuite(
  row: SphinxSuiteSummary | SphinxSuite,
  extra: { latestRunId?: string; runCounter?: number } = {}
): EvalSuite & { yaml?: string; caseCount?: number } {
  const meta = readSuiteMeta(row.id);
  return {
    createdBy: OPERATOR_USER._id,
    projectId: DEFAULT_PROJECT_ID,
    description: "",
    configRevision: "",
    environment: { servers: [] },
    source: "ui",
    ...meta,
    ...(meta.hostAttachments
      ? { hostAttachments: normalizeHostAttachments(meta.hostAttachments) }
      : {}),
    ...(meta.serverAttachment
      ? {
          serverAttachment: {
            ...meta.serverAttachment,
            resolvedServerNames: meta.serverAttachment.resolvedServerNames ?? [],
          },
        }
      : {}),
    _id: row.id,
    name: row.name,
    createdAt: toMs((row as SphinxSuite).createdAt ?? row.updatedAt),
    updatedAt: toMs(row.updatedAt),
    ...extra,
    ...("yaml" in row ? { yaml: row.yaml } : {}),
    ...("caseCount" in row ? { caseCount: row.caseCount } : {}),
  };
}

// caseId → { suiteId, json } so update/duplicate/iteration-snapshot lookups
// don't need an endpoint the API doesn't have (GET /cases/:id).
const caseCache = new Map<string, { suiteId: string; json: Record<string, unknown> }>();

const caseJson = (row: SphinxCase): Record<string, unknown> =>
  row.json && typeof row.json === "object"
    ? (row.json as Record<string, unknown>)
    : {};

export function toEvalCase(row: SphinxCase): EvalCase {
  const json = caseJson(row);
  caseCache.set(row.id, { suiteId: row.suiteId, json });
  return {
    createdBy: OPERATOR_USER._id,
    projectId: DEFAULT_PROJECT_ID,
    title: "",
    query: "",
    models: [],
    runs: 1,
    expectedToolCalls: [],
    ...(json as Partial<EvalCase>),
    _id: row.id,
    testSuiteId: row.suiteId,
    _creationTime: toMs(row.createdAt),
  } as EvalCase;
}

function runSummary(iterations: SphinxIteration[]) {
  let passed = 0;
  let failed = 0;
  for (const it of iterations) {
    const r = deriveResult(it);
    if (r === "passed") passed += 1;
    else if (r === "failed") failed += 1;
  }
  const total = iterations.length;
  return {
    total,
    passed,
    failed,
    passRate: total > 0 ? passed / total : 0,
  };
}

export function toEvalSuiteRun(
  row: SphinxRun,
  runNumber: number
): EvalSuiteRun & {
  targets: SphinxRun["targets"];
  yarnEnv: SphinxRun["yarnEnv"];
  yarnOrg: SphinxRun["yarnOrg"];
} {
  const iterations = row.iterations ?? [];
  const status = mapRunStatus(row.status);
  // Iterations (when hydrated) are the freshest source; otherwise the run
  // row's `summary` (written by the queue on completion).
  const summary =
    iterations.length > 0
      ? runSummary(iterations)
      : row.summary
        ? {
            ...row.summary,
            passRate: row.summary.total > 0 ? row.summary.passed / row.summary.total : 0,
          }
        : undefined;
  return {
    _id: row.id,
    suiteId: row.suiteId,
    createdBy: OPERATOR_USER._id,
    projectId: DEFAULT_PROJECT_ID,
    runNumber,
    configRevision: "",
    configSnapshot: { tests: [], environment: { servers: [] } },
    status,
    summary,
    result:
      status === "completed" && summary
        ? summary.failed > 0
          ? "failed"
          : "passed"
        : status === "cancelled"
          ? "cancelled"
          : status === "failed"
            ? "failed"
            : "pending",
    source: "ui",
    notes: row.note ?? undefined,
    createdAt: toMs(row.createdAt),
    completedAt: row.finishedAt ? toMs(row.finishedAt) : undefined,
    isActive: isLive(row.status),
    expectedIterations: iterations.length || undefined,
    targets: row.targets,
    yarnEnv: row.yarnEnv,
    yarnOrg: row.yarnOrg,
  };
}

export function toEvalIteration(
  it: SphinxIteration,
  run?: SphinxRun,
  index = 0
): EvalIteration {
  const cached = caseCache.get(it.caseId);
  const json = cached?.json ?? {};
  const createdAt = toMs(it.startedAt) || toMs(run?.createdAt);
  const updatedAt = toMs(it.finishedAt) || createdAt;
  return {
    _id: it.id,
    testCaseId: it.caseId,
    projectId: DEFAULT_PROJECT_ID,
    testCaseSnapshot: {
      title: String(json.title ?? ""),
      query: String(json.query ?? ""),
      provider: "sphinx",
      model: it.target,
      runs: 1,
      expectedToolCalls: (json.expectedToolCalls as EvalCase["expectedToolCalls"]) ?? [],
      ...(json.steps ? { steps: json.steps as EvalCase["steps"] } : {}),
      ...(json.isNegativeTest !== undefined ? { isNegativeTest: Boolean(json.isNegativeTest) } : {}),
      ...(json.expectedOutput !== undefined ? { expectedOutput: String(json.expectedOutput) } : {}),
    },
    suiteRunId: it.runId,
    trigger: "suite",
    createdBy: OPERATOR_USER._id,
    createdAt,
    startedAt: it.startedAt ? toMs(it.startedAt) : undefined,
    iterationNumber: index + 1,
    updatedAt,
    blob: it.clientTranscript ? it.id : undefined,
    status: mapIterationStatus(it.status),
    result: deriveResult(it),
    // "reported" makes computeIterationResult TRUST this result. Without it,
    // the fork's legacy client-side matcher re-derives pass/fail from
    // actualToolCalls/testCaseSnapshot — both empty for sphinx iterations —
    // and vacuously passes every completed iteration: the runs list showed
    // 100% while the run detail (which reads grades) showed failures.
    resultSource: "reported",
    actualToolCalls: [],
    tokensUsed: 0,
    metadata: {
      target: it.target,
      grade: it.grade,
      serverTruth: it.serverTruth,
      artifactPrefix: it.artifactPrefix,
    },
  };
}

// ---- fetchers ---------------------------------------------------------------

/** Suite runs (newest first) with iterations hydrated for the most recent N. */
async function loadSuiteRunsWithIterations(suiteId: string): Promise<SphinxRun[]> {
  const runs = await sphinxApi.runs.list(suiteId);
  const hydrated = await Promise.all(
    runs.slice(0, RUNS_WITH_ITERATIONS_CAP).map((r) => sphinxApi.runs.get(r.id))
  );
  return [...hydrated, ...runs.slice(RUNS_WITH_ITERATIONS_CAP)];
}

/** Oldest run is #1; REST lists newest first. */
const numberRuns = (runs: SphinxRun[]) => {
  const total = runs.length;
  return runs.map((r, i) => toEvalSuiteRun(r, total - i));
};

async function loadOverview(): Promise<EvalSuiteOverviewEntry[]> {
  const [suites, runs] = await Promise.all([
    sphinxApi.suites.list(),
    sphinxApi.runs.list(),
  ]);
  const bySuite = new Map<string, SphinxRun[]>();
  for (const run of runs) {
    const list = bySuite.get(run.suiteId) ?? [];
    list.push(run);
    bySuite.set(run.suiteId, list);
  }
  return suites.map((s) => {
    const suiteRuns = numberRuns(bySuite.get(s.id) ?? []);
    const latestRun = suiteRuns[0] ?? null;
    return {
      suite: toEvalSuite(s, {
        latestRunId: latestRun?._id,
        runCounter: suiteRuns.length,
      }),
      latestRun,
      recentRuns: suiteRuns.slice(0, 5),
      passRateTrend: [],
      totals: { passed: 0, failed: 0, runs: suiteRuns.length },
    };
  });
}

async function loadSuiteDetails(suiteId: string): Promise<SuiteDetailsQueryResponse> {
  const [suite, runs] = await Promise.all([
    sphinxApi.suites.get(suiteId),
    loadSuiteRunsWithIterations(suiteId),
  ]);
  const testCases = (suite.cases ?? []).map(toEvalCase);
  const iterations: EvalIteration[] = [];
  for (const run of runs) {
    (run.iterations ?? []).forEach((it, i) =>
      iterations.push(toEvalIteration(it, run, i))
    );
  }
  return { testCases, iterations };
}

const anyLiveRun = (runs: Array<{ status: string }>) =>
  runs.some((r) => isLive(r.status)) ? LIVE_POLL_MS : null;

// ---- testSuites:* queries ---------------------------------------------------

/** testSuites:getTestSuitesOverview */
export function useTestSuitesOverview(args: Record<string, unknown> | "skip") {
  return useSphinxQuery(args === "skip" ? null : "evals:overview", loadOverview, {
    pollMs: (data) =>
      data.some((e) => e.latestRun && isLive(e.latestRun.status)) ? LIVE_POLL_MS : null,
  }) as EvalSuiteOverviewEntry[] | undefined;
}

/** testSuites:getAllTestCasesAndIterationsBySuite (reactive) */
export function useSuiteTestCasesAndIterations(
  args: { suiteId: string } | "skip"
) {
  const suiteId = args === "skip" ? null : args.suiteId;
  return useSphinxQuery(
    suiteId ? `evals:suiteDetails:${suiteId}` : null,
    () => loadSuiteDetails(suiteId!),
    { pollMs: (data) => anyLiveRun(data.iterations) }
  ) as SuiteDetailsQueryResponse | null | undefined;
}

/** testSuites:getAllTestCasesAndIterationsBySuite (one-shot) */
export function fetchSuiteTestCasesAndIterations(
  _client: DataClient,
  args: { suiteId: string }
) {
  return loadSuiteDetails(args.suiteId);
}

/** testSuites:listTestSuiteRuns */
export function useTestSuiteRuns(
  args: { suiteId: string; limit?: number } | "skip"
) {
  const suiteId = args === "skip" ? null : args.suiteId;
  const limit = args === "skip" ? undefined : args.limit;
  return useSphinxQuery(
    suiteId ? `evals:runs:${suiteId}` : null,
    async () => {
      const runs = numberRuns(await loadSuiteRunsWithIterations(suiteId!));
      return limit ? runs.slice(0, limit) : runs;
    },
    { pollMs: anyLiveRun }
  ) as EvalSuiteRun[] | undefined;
}

const loadCases = async (suiteId: string) =>
  (await sphinxApi.cases.list(suiteId)).map(toEvalCase);

/** testSuites:listTestCases (reactive) */
export function useTestCases(args: { suiteId: string } | "skip") {
  const suiteId = args === "skip" ? null : args.suiteId;
  return useSphinxQuery(suiteId ? `evals:cases:${suiteId}` : null, () =>
    loadCases(suiteId!)
  ) as EvalCase[] | undefined;
}

/** testSuites:listTestCases (one-shot) */
export function fetchTestCases(_client: DataClient, args: { suiteId: string }) {
  return loadCases(args.suiteId);
}

/** testSuites:listTestIterations (one-shot) — all iterations for a case. */
export async function fetchTestIterations(
  _client: DataClient,
  args: { testCaseId: string }
): Promise<EvalIteration[]> {
  const cached = caseCache.get(args.testCaseId);
  if (!cached) return [];
  const runs = await loadSuiteRunsWithIterations(cached.suiteId);
  const out: EvalIteration[] = [];
  for (const run of runs) {
    (run.iterations ?? []).forEach((it, i) => {
      if (it.caseId === args.testCaseId) out.push(toEvalIteration(it, run, i));
    });
  }
  return out;
}

const loadIteration = async (iterationId: string) =>
  toEvalIteration(await sphinxApi.iterations.get(iterationId));

/** testSuites:getTestIteration (reactive) */
export function useTestIteration(args: { iterationId: string } | "skip") {
  const id = args === "skip" ? null : args.iterationId;
  return useSphinxQuery(id ? `evals:iteration:${id}` : null, () => loadIteration(id!), {
    pollMs: (it) => (isLive(it.status) ? LIVE_POLL_MS : null),
  }) as EvalIteration | null | undefined;
}

/** testSuites:getTestIteration (one-shot) */
export function fetchTestIteration(
  _client: DataClient,
  args: { iterationId: string }
) {
  return loadIteration(args.iterationId);
}

/** testSuites:getTestSuite */
export function useTestSuite(args: { suiteId: string } | "skip") {
  const id = args === "skip" ? null : args.suiteId;
  return useSphinxQuery(id ? `evals:suite:${id}` : null, async () =>
    toEvalSuite(await sphinxApi.suites.get(id!))
  ) as (EvalSuite & { yaml?: string }) | null | undefined;
}

// ---- testSuites:* mutations --------------------------------------------------

const SUITE_COLUMNS = new Set(["name", "yaml"]);

const createTestSuite = withInvalidate(
  async (args: Record<string, unknown> & { name: string }): Promise<{ _id: string }> => {
    const created = await sphinxApi.suites.create({
      name: args.name,
      ...(typeof args.yaml === "string" ? { yaml: args.yaml } : {}),
    });
    const meta: SuiteMeta = {};
    for (const [k, v] of Object.entries(args)) {
      if (k === "projectId" || SUITE_COLUMNS.has(k) || v === undefined) continue;
      (meta as Record<string, unknown>)[k] = v;
    }
    if (Object.keys(meta).length > 0) writeSuiteMeta(created.id, meta);
    return { _id: created.id };
  }
);

const updateTestSuite = withInvalidate(
  async (args: Record<string, unknown> & { suiteId: string }): Promise<void> => {
    const { suiteId, ...patch } = args;
    const columns: { name?: string; yaml?: string } = {};
    const meta: SuiteMeta = {};
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) continue;
      if (k === "name" && typeof v === "string") columns.name = v;
      else if (k === "yaml" && typeof v === "string") columns.yaml = v;
      else (meta as Record<string, unknown>)[k] = v;
    }
    if (Object.keys(meta).length > 0) writeSuiteMeta(suiteId, meta);
    if (Object.keys(columns).length > 0) await sphinxApi.suites.patch(suiteId, columns);
  }
);

const deleteTestSuite = withInvalidate(async (args: { suiteId: string }) => {
  await sphinxApi.suites.delete(args.suiteId);
});

const duplicateTestSuite = withInvalidate(
  async (args: { suiteId: string }): Promise<{ _id: string }> => {
    const copy = await sphinxApi.suites.duplicate(args.suiteId);
    const meta = readSuiteMeta(args.suiteId);
    if (Object.keys(meta).length > 0) writeSuiteMeta(copy.id, meta);
    return { _id: copy.id };
  }
);

const deleteTestSuiteRun = withInvalidate(async (args: { runId: string }) => {
  await sphinxApi.runs.delete(args.runId);
});

const cancelTestSuiteRun = withInvalidate(async (args: { runId: string }) => {
  await sphinxApi.runs.cancel(args.runId);
});

// ---- sphinx runs (case × target matrix) --------------------------------------

export type SphinxRunInput = {
  targets: TargetId[];
  yarnEnv: YarnEnv;
  yarnOrg: YarnOrg;
  note?: string;
  /** Restrict to these cases (per-case quick run); omitted = whole suite. */
  caseIds?: string[];
};

/** POST /eval-suites/:id/runs — one iteration per case × target, jobs enqueued. */
export const createSphinxRun = withInvalidate(
  (suiteId: string, input: SphinxRunInput) => sphinxApi.runs.create(suiteId, input)
);

/** POST /runs/:id/cancel */
export const cancelSphinxRun = withInvalidate((runId: string) =>
  sphinxApi.runs.cancel(runId)
);

/** GET /runs/:id (run + iterations), polling every 2s while anything is live. */
export function useSphinxRun(runId: string | null) {
  return useSphinxQuery(
    runId ? `sphinx:run:${runId}` : null,
    () => sphinxApi.runs.get(runId!),
    {
      pollMs: (run) =>
        isLive(run.status) || run.iterations.some((it) => isLive(it.status))
          ? LIVE_POLL_MS
          : null,
    }
  ) as (SphinxRun & { iterations: SphinxIteration[] }) | null | undefined;
}

const CASE_ARG_KEYS_TO_DROP = new Set(["suiteId", "testCaseId", "projectId"]);

function caseJsonFromArgs(
  args: Record<string, unknown>,
  base: Record<string, unknown> = {}
): Record<string, unknown> {
  const next = { ...base };
  for (const [k, v] of Object.entries(args)) {
    if (CASE_ARG_KEYS_TO_DROP.has(k) || v === undefined) continue;
    if (v === null) delete next[k];
    else next[k] = v;
  }
  return next;
}

const createTestCase = withInvalidate(
  async (args: Record<string, unknown> & { suiteId: string }): Promise<string> => {
    const created = await sphinxApi.cases.create(args.suiteId, caseJsonFromArgs(args));
    caseCache.set(created.id, { suiteId: args.suiteId, json: caseJson(created) });
    return created.id;
  }
);

const updateTestCase = withInvalidate(
  async (args: Record<string, unknown> & { testCaseId: string }): Promise<void> => {
    const cached = caseCache.get(args.testCaseId);
    const json = caseJsonFromArgs(args, cached?.json);
    const updated = await sphinxApi.cases.patch(args.testCaseId, { json });
    caseCache.set(updated.id, { suiteId: updated.suiteId, json: caseJson(updated) });
  }
);

const deleteTestCase = withInvalidate(async (args: { testCaseId: string }) => {
  await sphinxApi.cases.delete(args.testCaseId);
  caseCache.delete(args.testCaseId);
});

const duplicateTestCase = withInvalidate(
  async (args: { testCaseId: string }): Promise<{ _id: string }> => {
    const cached = caseCache.get(args.testCaseId);
    if (!cached) throw new Error("Test case not loaded; open the suite and retry.");
    const json = { ...cached.json, title: `${String(cached.json.title ?? "Untitled")} (copy)` };
    const created = await sphinxApi.cases.create(cached.suiteId, json);
    caseCache.set(created.id, { suiteId: cached.suiteId, json });
    return { _id: created.id };
  }
);

const unsupported = (what: string) => async (..._args: unknown[]): Promise<never> => {
  throw new Error(`${what} is not available in sphinx.`);
};

/** testSuites:createTestSuite */
export function useCreateTestSuiteMutation() {
  return createTestSuite;
}

/** testSuites:updateTestSuite */
export function useUpdateTestSuiteMutation() {
  return updateTestSuite;
}

/** testSuites:deleteTestSuite */
export function useDeleteTestSuiteMutation() {
  return deleteTestSuite;
}

/** testSuites:duplicateTestSuite */
export function useDuplicateTestSuiteMutation() {
  return duplicateTestSuite;
}

/** testSuites:deleteTestSuiteRun */
export function useDeleteTestSuiteRunMutation() {
  return deleteTestSuiteRun;
}

/** testSuites:cancelTestSuiteRun */
export function useCancelTestSuiteRunMutation() {
  return cancelTestSuiteRun;
}

/** testSuites:createTestCase */
export function useCreateTestCaseMutation() {
  return createTestCase;
}

/** testSuites:updateTestCase */
export function useUpdateTestCaseMutation() {
  return updateTestCase;
}

/** testSuites:deleteTestCase */
export function useDeleteTestCaseMutation() {
  return deleteTestCase;
}

/** testSuites:duplicateTestCase */
export function useDuplicateTestCaseMutation() {
  return duplicateTestCase;
}

const generateEvalAttachmentUploadUrl = unsupported("Eval attachment upload");
const setTestCaseAttachments = unsupported("Eval attachments");

/** testSuites:generateEvalAttachmentUploadUrl */
export function useGenerateEvalAttachmentUploadUrlMutation() {
  return generateEvalAttachmentUploadUrl;
}

/** testSuites:setTestCaseAttachments */
export function useSetTestCaseAttachmentsMutation() {
  return setTestCaseAttachments;
}

// ---- testSuites:* actions ----------------------------------------------------

const getTestIterationBlob = async (args: { iterationId: string }): Promise<unknown> => {
  const it = await sphinxApi.iterations.get(args.iterationId);
  return it.clientTranscript ?? null;
};

const getTestSuiteRunDiff = unsupported("Run diff");

type PromoteArgs = {
  projectId?: string | null;
  testCaseTitle?: string;
  title?: string;
  query?: string;
  destinationSuiteId?: string;
  newSuiteName?: string;
  [key: string]: unknown;
};

/** Shared by the chat → test-case promotions: resolve/create the suite, add the case. */
async function promoteToTestCase(args: PromoteArgs) {
  let suiteId = args.destinationSuiteId;
  if (!suiteId) {
    const created = await createTestSuite({ name: args.newSuiteName || "Promoted cases" });
    suiteId = created._id;
  }
  const testCaseId = await createTestCase({
    suiteId,
    title: args.testCaseTitle ?? args.title ?? "Untitled case",
    query: args.query ?? "",
    models: [],
    runs: 1,
    expectedToolCalls: [],
  });
  return { suiteId, testCaseId, addedServers: [], updatedSuiteEnvironment: false };
}

/** testSuites:getTestIterationBlob */
export function useGetTestIterationBlobAction() {
  return getTestIterationBlob;
}

/** testSuites:getTestSuiteRunDiff */
export function useGetTestSuiteRunDiffAction() {
  return getTestSuiteRunDiff;
}

/** testSuites:saveAsTestCaseFromChatMessage */
export function useSaveAsTestCaseFromChatMessageAction() {
  return promoteToTestCase;
}

/** testSuites:importChatSessionToTestCase */
export function useImportChatSessionToTestCaseAction() {
  return promoteToTestCase;
}

// ---- traceExport:* -----------------------------------------------------------

const exportSessionTraces = unsupported("Trace export");
const exportProjectTracesPage = unsupported("Trace export");

/** traceExport:exportSessionTraces */
export function useExportSessionTracesAction() {
  return exportSessionTraces;
}

/** traceExport:exportProjectTracesPage */
export function useExportProjectTracesPageAction() {
  return exportProjectTracesPage;
}
